import { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { colors } from 'styles/colors';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { fetchLatestTrending, TrendingItem } from '../../../services/contentfulApi';
import TrendingSkeleton from '__mocks__/skeletonLoading/TrendingSkeleton';

const Trendings = () => {
  const [trendingData, setTrendingData] = useState<TrendingItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTrendingData = async () => {
      try {
        setLoading(true);
        const data = await fetchLatestTrending();
        setTrendingData(data);
        setError(null);
      } catch (err) {
        console.error('Failed to load trending data:', err);
        setError('Failed to load trending articles');
      } finally {
        setLoading(false);
      }
    };

    loadTrendingData();
  }, []);

  const handleClick = (link: string) => {
    window.open(link, '_blank');
  };

  if (loading) {
    return (
        <Box>
        <TrendingSkeleton count={5} />
        </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          sx={{
            fontSize: 14,
            color: colors.text.approxSuvaGrey,
            textAlign: 'center',
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  if (trendingData.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          sx={{
            fontSize: 14,
            color: colors.text.approxSuvaGrey,
            textAlign: 'center',
          }}
        >
          No trending articles available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        mt: '45px',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          position: 'fixed',
          zIndex: 999,
          background: 'white',
          height: 40,
          width: '70%',
          top:0,
          mb:2,
        }}
      >
        <TrendingUpIcon sx={{ color: colors.text.primaryTheme, fontSize: 18 }} />
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: colors.text.primaryBlack,
          }}
        >
          Trending Independent Press Articles
        </Typography>
      </Box>

      {trendingData.map((item) => (
        <Box
          key={item.id}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            overflow: 'hidden',
            background: colors.background.approxAliceBlue,
            borderRadius: 2,
            '&:hover': {
              opacity: 0.9,
            },
          }}
          onClick={() => handleClick(item.link)}
        >
          <Box
            component="img"
            src={item.imageUrl}
            alt={item.title}
            sx={{
              width: '100%',
              height: 130,
              objectFit: 'cover',
              borderRadius: '8px 8px 0 0',
            }}
          />

          <Box sx={{ p: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.text.primaryTheme,
                }}
              >
                {item.category}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  color: colors.text.approxSuvaGrey,
                }}
              >
                {item.timeAgo}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: colors.text.primaryBlack,
                lineHeight: '18px',
                mb: 0.5,
              }}
            >
              {item.title}
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                color: colors.text.approxSuvaGrey,
                lineHeight: '16px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.description}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default Trendings;